import type { Db } from "./client";
import { getUserId } from "./client";
import { throwIfPgError, NotFoundError } from "./errors";
import type {
  GardenRow,
  GardenMembershipRow,
  GardenMembershipInsert,
} from "@garden/types/db";

/** All gardens the current user is a member of, ordered by oldest-joined
 *  first (so the bootstrap garden tends to come first when the user has
 *  multiple). Honors RLS — `select on garden` is gated by
 *  `is_garden_member(id)`. */
export async function listMyGardens(client: Db): Promise<GardenRow[]> {
  const { data, error } = await client
    .from("garden")
    .select("*")
    .order("created_at", { ascending: true });
  throwIfPgError(error);
  return (data ?? []) as GardenRow[];
}

/** Members of the given garden. Used by the share panel to show "you and
 *  N others". Returns the membership rows, not the user records — Supabase
 *  doesn't let us join into auth.users from PostgREST. */
export async function listMembers(
  client: Db,
  gardenId: string,
): Promise<GardenMembershipRow[]> {
  const { data, error } = await client
    .from("garden_membership")
    .select("*")
    .eq("garden_id", gardenId)
    .order("joined_at", { ascending: true });
  throwIfPgError(error);
  return (data ?? []) as GardenMembershipRow[];
}

/** Join a garden by its `join_code`. Returns the garden row.
 *
 *  Two-step:
 *    1. Look up the garden by code (RLS lets us see only gardens we're
 *       already in OR whose code we know — but the SELECT policy uses
 *       `is_garden_member(id)`, so an outsider can't read the row by
 *       code alone). We therefore do this read with a SECURITY DEFINER
 *       Postgres function so anyone can probe a code, but get no row
 *       back unless it matches.
 *
 *  Until we add such a function, we use a workaround: insert the
 *  membership first (RLS lets us insert iff `user_id = auth.uid()`),
 *  then read the garden. If the code doesn't match any garden, the FK
 *  on `garden_membership.garden_id` won't apply because we haven't
 *  resolved an id yet. So we DO need a way to resolve code → id without
 *  RLS.
 *
 *  The pragmatic answer: a Postgres function `join_garden_by_code(code)`
 *  that runs as SECURITY DEFINER, looks up the garden, inserts the
 *  membership, and returns the garden row. We add it inline here as a
 *  small fallback: try the function call; if the function isn't yet in
 *  the DB (migration not applied), surface a clear error.
 */
export async function joinGardenByCode(
  client: Db,
  code: string,
): Promise<GardenRow> {
  const trimmed = code.trim();
  if (trimmed === "") {
    throw new Error("Join code is empty");
  }
  const { data, error } = await client.rpc("join_garden_by_code", {
    code: trimmed,
  });
  throwIfPgError(error);
  // A `RETURNS garden` function returning SQL NULL gives PostgREST a row
  // with every column null, not a JSON null. Detect that explicitly.
  const row = data as Partial<GardenRow> | null;
  if (!row || row.id == null) {
    throw new NotFoundError("garden", `code=${trimmed}`);
  }
  return row as GardenRow;
}

/** Remove the current user from a garden. After leaving, the user no
 *  longer has visibility into any rows attributed to that garden. */
export async function leaveGarden(
  client: Db,
  gardenId: string,
): Promise<void> {
  const userId = await getUserId(client);
  const { error } = await client
    .from("garden_membership")
    .delete()
    .eq("garden_id", gardenId)
    .eq("user_id", userId);
  throwIfPgError(error);
}

/** Generate a new 8-char join code for a garden. Anyone in the garden
 *  can do this. The old code stops working immediately (single-column,
 *  not a token table). */
export async function regenerateJoinCode(
  client: Db,
  gardenId: string,
): Promise<GardenRow> {
  const code = randomJoinCode();
  const { data, error } = await client
    .from("garden")
    .update({ join_code: code })
    .eq("id", gardenId)
    .select("*")
    .maybeSingle();
  throwIfPgError(error);
  if (!data) throw new NotFoundError("garden", gardenId);
  return data as GardenRow;
}

/** Add the calling user as a member of a garden they just created.
 *  Called immediately after `createGarden` to put the creator in the
 *  garden — without this, the new garden has zero members and is
 *  invisible (`select` policy uses `is_garden_member`). */
export async function addSelfMembership(
  client: Db,
  gardenId: string,
): Promise<void> {
  const userId = await getUserId(client);
  const { error } = await client
    .from("garden_membership")
    .insert({ garden_id: gardenId, user_id: userId } as GardenMembershipInsert)
    .select("garden_id")
    .maybeSingle();
  if (error && error.code !== "23505") {
    // 23505 = unique violation: already a member, idempotent ok
    throwIfPgError(error);
  }
}

function randomJoinCode(): string {
  // 8 hex chars from crypto.getRandomValues. Matches the migration's
  // shape (substr(md5(...), 1, 8)) but avoids relying on the server
  // default — explicit code = single source of truth at the call site.
  const bytes = new Uint8Array(4);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
